<template>
  <div class="service-list-page" :class="pageClass">
    <section class="service-search-hero">
      <div class="service-search-hero__desktop">
        <slot
          name="search"
          :keyword="localKeyword"
          :placeholder="placeholder"
          :search="handleHeroSearch"
        >
          <Search
            :title="title"
            :keyword="localKeyword"
            :placeholder="placeholder"
            :search-button-text="searchButtonText"
            :enable-suggestions="enableSearchSuggestions"
            :suggestions="searchSuggestions"
            :suggestions-loading="searchSuggestionsLoading"
            :suggestions-error="searchSuggestionsError"
            @update:keyword="handleKeywordUpdate"
            @search="handleHeroSearch"
            @focus="handleSearchFocus"
            @suggestion-select="handleSuggestionSelect"
            @suggestion-retry="emit('suggestion-retry')"
          />
        </slot>
      </div>

      <div class="service-search-hero__mobile">
        <slot
          name="mobile-hero"
          :keyword="localKeyword"
          :placeholder="placeholder"
          :open-search="openSearchPanel"
          :filter="emitFilter"
        >
          <div class="service-search-hero__mobile-title">{{ title }}</div>
          <div class="service-search-hero__mobile-row">
            <button
              type="button"
              class="service-search-hero__mobile-search"
              @click="openSearchPanel"
            >
              <SvgIcon name="search" size="16" class="service-search-hero__mobile-search-icon" />
              <span
                class="service-search-hero__mobile-search-text"
                :class="{ 'service-search-hero__mobile-search-text--value': localKeyword }"
              >
                {{ localKeyword || placeholder }}
              </span>
            </button>
            <button
              v-if="showMobileFilter"
              type="button"
              class="service-search-hero__filter"
              :class="{ 'service-search-hero__filter--active': filterActive }"
              @click="emitFilter"
            >
              <SvgIcon name="filter" size="20" class="service-search-hero__filter-icon" />
              <span>{{ filterText }}</span>
            </button>
          </div>
        </slot>
      </div>

      <MobileSearchPanel
        v-if="!$slots['mobile-hero']"
        v-model:visible="searchPanelVisible"
        :placeholder="placeholder"
        :initial-keyword="localKeyword"
        :show-history="showSearchHistory"
        :history-items="mobileSearchHistory"
        :enable-suggestions="enableSearchSuggestions"
        :suggestions="searchSuggestions"
        :suggestions-loading="searchSuggestionsLoading"
        :suggestions-error="searchSuggestionsError"
        @search="handleMobileSearch"
        @focus="handleSearchFocus"
        @keyword-change="handleKeywordUpdate"
        @suggestion-select="handleSuggestionSelect"
        @suggestion-retry="emit('suggestion-retry')"
        @clear-history="confirmClearMobileSearchHistory"
      />
    </section>

    <section
      class="service-list-page__main"
      :class="{ 'service-list-page__main--empty': emptyState }"
    >
      <div
        class="service-list-page__body"
        :class="{ 'service-list-page__body--wide': wideContent }"
      >
        <div v-if="!wideContent" class="service-list-page__sidebar-column">
          <aside class="service-list-page__sidebar">
            <slot name="sidebar" />
          </aside>
        </div>

        <main
          class="service-list-page__content"
          :class="[contentClass, { 'service-list-page__content--empty': emptyState }]"
        >
          <slot />
        </main>
      </div>
    </section>

    <slot name="after" />
  </div>
</template>

<script setup>
/**
 * @description 服务支持检索页通用骨架，供帮助中心/下载中心复用。
 */
import { computed, ref, watch } from 'vue'
import Search from '~/components/website/service/Search.vue'
import MobileSearchPanel from '~/components/mobile/SearchPanel.vue'
import { useLocalMobileSearchHistory } from '~/composables/useLocalMobileSearchHistory'
import CommonModal from '~/utils/commonModal'
import { BaseMessage } from '~/utils/baseMessage'

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  keyword: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '请输入您想了解的问题',
  },
  searchButtonText: {
    type: String,
    default: '查询',
  },
  enableSearchSuggestions: {
    type: Boolean,
    default: false,
  },
  searchSuggestions: {
    type: Array,
    default: () => [],
  },
  searchSuggestionsLoading: {
    type: Boolean,
    default: false,
  },
  searchSuggestionsError: {
    type: Boolean,
    default: false,
  },
  filterActive: {
    type: Boolean,
    default: false,
  },
  filterText: {
    type: String,
    default: '筛选',
  },
  showMobileFilter: {
    type: Boolean,
    default: true,
  },
  pageClass: {
    type: [String, Array, Object],
    default: '',
  },
  contentClass: {
    type: [String, Array, Object],
    default: '',
  },
  mobileSearchType: {
    type: String,
    default: 'help',
  },
  showSearchHistory: {
    type: Boolean,
    default: true,
  },
  emptyState: {
    type: Boolean,
    default: false,
  },
  wideContent: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits([
  'update:keyword',
  'search',
  'search-focus',
  'filter',
  'suggestion-select',
  'suggestion-retry',
])
const searchPanelVisible = ref(false)
const {
  historyItems: mobileSearchHistory,
  loadHistory: loadMobileSearchHistory,
  addHistory: addMobileSearchHistory,
  clearHistory: clearMobileSearchHistory,
} = useLocalMobileSearchHistory(computed(() => props.mobileSearchType))

const localKeyword = computed({
  get: () => props.keyword,
  set: (value) => emit('update:keyword', value),
})

const handleHeroSearch = (value) => {
  emit('update:keyword', value)
  emit('search', value)
}

const handleKeywordUpdate = (value) => {
  emit('update:keyword', value)
}

const handleMobileSearch = (value) => {
  const keyword = String(value || '').trim()
  addMobileSearchHistory(keyword)
  emit('update:keyword', keyword)
  emit('search', keyword)
}

const confirmClearMobileSearchHistory = () => {
  CommonModal.confirm({
    title: '确认清除',
    content: '确定要清除全部搜索历史吗？',
    type: 'warning',
    okText: '确定',
    cancelText: '取消',
    onOk: () => {
      clearMobileSearchHistory()
      BaseMessage.success('清除成功')
    },
  })
}

const handleSearchFocus = (value) => {
  emit('search-focus', value)
}

const openSearchPanel = () => {
  searchPanelVisible.value = true
}

watch(searchPanelVisible, (visible) => {
  if (visible && props.showSearchHistory) loadMobileSearchHistory()
})

const emitFilter = () => {
  emit('filter')
}

const handleSuggestionSelect = (item) => {
  emit('suggestion-select', item)
}
</script>

<style scoped lang="scss">
.service-list-page {
  background: linear-gradient(180deg, #fff5f0 0%, #f7f7f7 100%);
  min-height: 100vh;
  padding-bottom: 32px;

  @include respond-to-max('lg') {
    min-height: auto;
    padding-bottom: 24px;
  }

  &__main {
    margin-top: 0;

    &--empty {
      @include respond-to('lg') {
        .service-list-page__body {
          min-height: 950px;
        }
      }
    }
  }

  &__body {
    display: grid;
    grid-template-columns: 296px minmax(0, 912px);
    gap: 32px;
    align-items: stretch;
    max-width: $container-max-width;
    margin: 0 auto;
    padding: 0 80px;
    box-sizing: border-box;

    @include respond-to-max('xl') {
      grid-template-columns: 296px minmax(0, 1fr);
      padding: 0 16px;
    }

    @include respond-to-max('lg') {
      display: block;
      gap: 16px;
      padding: 0 16px;
    }

    &--wide {
      display: block;

      .service-list-page__content {
        width: 100%;
        min-height: 0;
        overflow: visible;
      }
    }
  }

  &__sidebar-column {
    width: 100%;
    min-width: 0;

    @include respond-to('lg') {
      align-self: stretch;
      display: flex;
      flex-direction: column;
      min-height: 100%;
    }

    @include respond-to-max('lg') {
      display: none;
    }
  }

  &__sidebar {
    width: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    min-height: 0;

    @include respond-to('lg') {
      position: sticky;
      top: calc(#{$base-header-height} + 8px);
      z-index: 2;
      align-self: flex-start;
      width: 100%;
    }
  }

  &__content {
    flex: 1;
    min-width: 0;
    min-height: 0;
    overflow-x: hidden;

    @include respond-to('lg') {
      align-self: stretch;
      min-height: calc(100dvh - #{$base-header-height} - 12rem);
    }

    @include respond-to-max('lg') {
      margin: 0;
      padding: 0;
      border-radius: 8px;
    }

    &--empty {
      @include respond-to('lg') {
        align-self: flex-start;
        min-height: 0;
      }
    }
  }
}

.service-search-hero {
  &__desktop {
    :deep(.service-search) {
      background: transparent;
    }
  }

  &__mobile {
    display: none;
  }

  @include respond-to-max('lg') {
    &__desktop {
      display: none;
    }

    &__mobile {
      display: block;
      margin: 0;
    }

    &__mobile-title {
      padding: 12px 16px 0;
      box-sizing: border-box;
      color: $text-title-color;
      font-size: 20px;
      font-weight: 500;
      line-height: 28px;
      text-align: center;
    }

    &__mobile-row {
      display: flex;
      align-items: center;
      gap: 12px;
      height: 60px;
      padding: 12px 16px;
      box-sizing: border-box;
      margin-bottom: 4px;
    }

    &__mobile-search {
      display: flex;
      flex: 1;
      align-items: center;
      gap: 8px;
      min-width: 0;
      height: 36px;
      padding: 0 12px;
      border: none;
      border-radius: 6px;
      background: $bg-white;
      color: $text-placeholder-color;
      text-align: left;
    }

    &__mobile-search-icon {
      flex-shrink: 0;
      color: $text-subtitle-color;
    }

    &__mobile-search-text {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      color: $text-placeholder-color;
      font-size: 14px;
      line-height: 22px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__mobile-search-text--value {
      color: $text-title-color;
    }

    &__filter {
      display: flex;
      flex-shrink: 0;
      align-items: center;
      gap: 4px;
      height: 22px;
      padding: 0;
      border: none;
      background: transparent;
      color: $text-content-color;
      font-size: 14px;
      line-height: 22px;
    }

    &__filter-icon {
      font-size: 20px;
      color: $text-content-color;
    }

    &__filter--active {
      color: $primary-color;

      .service-search-hero__filter-icon {
        color: $primary-color;
      }
    }
  }
}
</style>
